import React, { createContext, useContext, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { DDD, E2Data, brandConfig, resetBrandConfig } from '../lib/ddd/ddd';
import { OrgIdContext } from './orgId_context';

// To hide the spinner and show nothing while the theme loads, set this to false
const SHOW_THEME_INIT_SPINNER = true;

export const ThemeContext = createContext();
export const Themes = {
    Default : null,
    Red : "red",
    Organization : "Organization",
    GVC : "GVC",
}

const ddd = new DDD(process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc");
const data = new E2Data();
data.entity = "IL_Themes";

export const ThemeProvider = ({ children }) => {
  const {sessionInfo, isSessionChecked} = useContext(OrgIdContext);
  const [theme, setTheme] = useState(Themes.Default);
  const [themeStored, setThemeStored] = useState(Themes.Default);
  const [cache, setCache] = useState({});
  const [elementRef, setElementRef] = useState({});
  const [preview, setPreview] = useState(false);
  const [logo, setLogo] = useState(null);
  const [changingTheme, setChangingTheme] = useState(() => {
    const noWaitPaths = ['integration', 'publicpresentation', 'public-api-playground'];
    const basePath = window.location.pathname.replace(/^\/|\/$/g, '').split('/')[0]?.toLowerCase();
    return !noWaitPaths.includes(basePath);
  });
  const [themeType, setThemeType] = useState(null);

  useEffect(() => {
    if (window.location.pathname.includes('dashboard-gvc')) return;
    if (!isSessionChecked) return;
    if (sessionInfo?.Extended?.Theme) {
      changeTheme(Themes.Organization);
    } else {
      setChangingTheme(false);
    }
  }, [isSessionChecked]);

  useEffect(() => {
    if (window.location.pathname.includes('dashboard-gvc')) {
      changeTheme(Themes.GVC);
    }
  }, []);

  const applyBrandConfigForTheme = (themeName) => {
    if (themeName === Themes.GVC) {
        brandConfig.methodPrefix = "EINV";
        brandConfig.dropNamespace = true;
        brandConfig.textReplacements = {
            "EUeInvoices.DDDI_": "EINV_",
            "DDDI_": "EINV_",
            "DDD Invoices": "GVC",
            "DDDInvoices": "GVC",
        };
    }
  };

  const  changeTheme = (_theme) => {
    if(themeType === _theme) return;
    resetBrandConfig();
    setThemeType(_theme);
    if(_theme === Themes.Organization){
        if(!sessionInfo?.Extended?.Theme){
          setTheme(Themes.Default);
          __setTheme(Themes.Default);
          setLogo(null);
        }

        if(cache[sessionInfo?.Extended?.Theme]){
            applyBrandConfigForTheme(sessionInfo?.Extended?.Theme);
            setTheme(sessionInfo?.Extended?.Theme);
            __setTheme(sessionInfo?.Extended?.Theme);
            setLogo(cache[sessionInfo?.Extended?.Theme].LogoUrl||null);
        }else{
            setChangingTheme(true);
            data.filter = `Name="${sessionInfo?.Extended?.Theme}"`;
            data.GetData().then(res => {
                if(res?.result?.data?.length > 0){
                    applyBrandConfigForTheme(res.result.data[0].Name);
                    setCache({...cache, [sessionInfo?.Extended?.Theme] : res.result.data[0]});
                    appendStylesToHead(res.result.data[0].Name, res.result.data[0].Content);
                    setTheme(res.result.data[0].Name);
                    __setTheme(res.result.data[0].Name);
                    setLogo(res.result.data[0].LogoUrl||null);
                }
                setTimeout(() => setChangingTheme(false), 200);
            });
        }
    }else if(_theme === Themes.GVC){
      applyBrandConfigForTheme(Themes.GVC);
      if(cache[Themes.GVC]){
          setTheme(Themes.GVC);
          __setTheme(Themes.GVC);
          setLogo(cache[Themes.GVC].LogoUrl||null);
      }else{
          setChangingTheme(true);
          data.filter = `Name="${Themes.GVC}"`;
          data.GetData().then(res => {
              if(res?.result?.data?.length > 0){
                  setCache({...cache, [sessionInfo?.Extended?.Theme] : res.result.data[0]});
                  appendStylesToHead(res.result.data[0].Name, res.result.data[0].Content);
                  setTheme(res.result.data[0].Name);
                  __setTheme(res.result.data[0].Name);
                  setLogo(res.result.data[0].LogoUrl||null);
              }
              setTimeout(() => setChangingTheme(false), 200);
          });
      }
    }else{
        setTheme(_theme);
        __setTheme(_theme);
    }
  };

  const __setTheme = (theme) => {
    document.body.className = getNameAsLowerCaseNoSpaces(theme);
  }

  const getNameAsLowerCaseNoSpaces = (name) => {
    if(!name) return null;
    return name?.toLowerCase().replace(/\s/g, '');
  }

  const appendStylesToHead = (themeName , theme) => {
    if(!themeName || !theme) return console.error("Theme name or content is missing");
    if(elementRef[themeName])
        elementRef[themeName].remove();

    let style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `body.${getNameAsLowerCaseNoSpaces(themeName)} { ${theme} }`;
    document.getElementsByTagName('head')[0].appendChild(style);
    setElementRef({...elementRef, [themeName] : style});
  }

  const setPreviewTheme = (theme) => {
    appendStylesToHead("preview", theme);
  }

  useEffect(() => {
    if(!preview){
      setTheme(themeStored);
      __setTheme(themeStored);
      elementRef["preview"]?.remove();
    }
    if(preview){
      setThemeStored(theme);
      setTheme("preview");
      __setTheme("preview");
    }
  },[preview])

  // ─── remove ThemeLoadingSpinner (and its import above) once you're happy ──────
  /*useEffect(() => {
        data.filter = `Hostname="${window.location.hostname}"`;
        data.GetData().then(res => {
            if(res?.result?.data?.length > 0){
                setCache({...cache, [window.location.hostname] : res.result.data[0].Content});
                appendStylesToHead(res.result.data[0].Name, res.result.data[0].Content);
                setTheme(res.result.data[0].Content);
                __setTheme(res.result.data[0].Content);
            }
        });
    }, []);*/


  return (
    <ThemeContext.Provider value={{ theme, changeTheme, cache, preview, setPreview, setPreviewTheme, logo, setLogo}}>
      {children}
      {changingTheme && SHOW_THEME_INIT_SPINNER && <ThemeLoadingOverlay />}
    </ThemeContext.Provider>
  );
};

const ThemeLoadingOverlay = () => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <CircularProgress />
  </div>
);